/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Sparkles,
  Calculator,
  MessageSquare,
  Ruler,
  BookOpen,
  BarChart3,
  Receipt,
  Layers,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "AI Built for HVAC Contractors",
  title: "AI for HVAC contractors",
  titleHighlight: "for install and service work.",
  description: `One platform where AI helps HVAC contractors bid faster, match costs to real supplier rates, audit scope before proposals ship, and run projects from one database. Built for 3–25 person teams. Grounded in your real business data — not generic construction averages.`,

  featureGrid: {
    title: "Where AI shows up for HVAC contractors",
    items: [
      {
        icon: Sparkles,
        title: "Equipment database",
        description:
          "Furnaces, AC units, heat pumps — tracked with current pricing from your suppliers.",
      },
      {
        icon: Sparkles,
        title: "Ductwork line items",
        description:
          "Draw ductwork runs and fitting counts on the blueprint; AI matches each line to your cost database.",
      },
      {
        icon: Sparkles,
        title: "Labor rate variance",
        description:
          "Residential vs. commercial vs. new construction — separate rates, separate estimates.",
      },
      {
        icon: Sparkles,
        title: "Install + service split",
        description:
          "New installs and service calls tracked in the same system with different workflows.",
      },
      {
        icon: Sparkles,
        title: "Multi-zone systems",
        description:
          "Track zoning, dampers, and controls as structured line items.",
      },
      {
        icon: Sparkles,
        title: "Hank for HVAC",
        description:
          "'What did we charge for a 3-ton install last month?' Real answer from your last 20 jobs.",
      },
    ],
  },

  workflow: {
    title: "How it works for HVAC contractors",
    description:
      "From first bid to final invoice — every step in one database, every AI decision reviewable.",
    steps: [
      {
        label: "Bid",
        title: "Equipment and labor in one estimate",
        description:
          "Draw ductwork runs on the mechanical drawing, add equipment from your supplier database, and AI matches every line to your cost database. Generate a branded proposal in one click.",
        details: [],
      },
      {
        label: "Install",
        title: "From accepted proposal to active job",
        description:
          "Accepted proposals become projects with crew assignments, equipment orders, and budget tracking. Track install progress and flag issues before they become callbacks.",
        details: [],
      },
      {
        label: "Service",
        title: "Recurring service tracked alongside installs",
        description:
          "Service calls live in the same workspace as installs so you can see the full customer history. Who you installed for, what equipment they have, when it was last serviced.",
        details: [],
      },
    ],
  },

  capabilities: [
    {
      icon: Sparkles,
      title: "Install and service in one workspace",
      description:
        "Most HVAC software forces you to pick between install estimating and service dispatching. Contractor Co-Pilot handles both in one system so a crew that does new construction, retrofit, and service calls can run everything from one database.",
      bullets: [
        "Unified install and service workflows",
        "Customer history across install and service",
        "Equipment tracking per property",
        "Separate labor rates per work type",
      ],
    },
    {
      icon: Sparkles,
      title: "Equipment database tied to real supplier prices",
      description:
        "HVAC equipment pricing changes quarterly. Contractor Co-Pilot lets you import supplier price lists from PDFs and spreadsheets so your estimates reflect what you're actually paying — not a list price from three years ago.",
      bullets: [
        "Supplier catalog imports",
        "Equipment categories with variants",
        "Margin tracking per equipment type",
        "Quote expiration tracking",
      ],
    },
    {
      icon: Sparkles,
      title: "Hank audits margin before you bid",
      description:
        "HVAC margins are thin. Hank audits every estimate against your historical jobs and flags anything with unusual margin — either way too low (you'll lose money) or way too high (you'll lose the bid).",
      bullets: [
        "Historical margin comparison",
        "Low-margin warnings",
        "Missing labor line detection",
        "Equipment cost verification",
      ],
    },
    {
      icon: Sparkles,
      title: "Built for small HVAC companies, not enterprise fleets",
      description:
        "No dispatch complexity you don't need. No 50-user enterprise pricing. Just the right tools for a 3–25 person HVAC business that wants estimating, project management, and AI in one place.",
      bullets: [
        "Right-sized for 3–25 person teams",
        "No per-tech dispatch pricing",
        "Mobile-friendly for field check-ins",
        "Multi-user with role-based access",
      ],
    },
  ],

  relatedFeatures: [
    { label: "HVAC Solutions", href: "/solutions/hvac", icon: Ruler },
    {
      label: "HVAC Calculator",
      href: "/tools/hvac-calculator",
      icon: Calculator,
    },
    {
      label: "HVAC Estimating Guide",
      href: "/blog/hvac-estimating-guide",
      icon: BookOpen,
    },
    { label: "CC vs PlanSwift", href: "/compare/planswift", icon: BarChart3 },
    {
      label: "Construction Estimating",
      href: "/features/estimates",
      icon: Receipt,
    },
    { label: "Blueprint Takeoffs", href: "/features/takeoffs", icon: Layers },
    {
      label: "AI Assistant (Hank)",
      href: "/features/ai-assistant",
      icon: MessageSquare,
    },
    { label: "All Features", href: "/features", icon: Sparkles },
  ],
};

export function AiForHvacContractorsPageClient() {
  return <FeaturePageTemplate config={config} />;
}
